import React from "react";
import { List } from "components";
import {
    StatusCell,
    AmountCell,
    ProjectCell,
    JobCell,
    DateCell,
    TypeCell,
    ExtendedNameCell,
} from "@/modules/work/billables/cells";
import { EmployeeCell } from "components/cell/employee/cell";
import { withPermissions } from "components/with-permisisons";
import { ROUTE } from "@/lib/utils";
import { ExtensionCell } from "@/modules/reports/billables/extension/cells";
import { usePermissions } from "contexts/permission";
import { getEmployeeFields } from "components/user/config";

const PendingBillablesList = ({ coordinatorId }) => {
    const permissions = usePermissions();

    const baseFields = [
        "id",
        "name",
        "type",
        "job.name",
        "job.project.name",
        "status",
        "billing_date",
        "due_date",
        "extensions.start_date",
        "extensions.billing_date",
        "extensions.justification",
        "extensions.date_created",
        "access.employee",
        "access.role",
        // company name under project -> client -> company
        {
            job: [
                {
                    project: ["client.company_id.name"],
                },
            ],
        },
        {
            job: [
                "id",
                getEmployeeFields("coordinator"),
                {
                    project: [
                        "id",
                        "name",
                        getEmployeeFields("coordinator"),
                        getEmployeeFields("sales_coordinator"),
                        getEmployeeFields("account_manager"),
                        "project_id",
                    ],
                },
            ],
        },
    ];

    const canReadAmount = permissions.hasFieldAccess(
        "billables",
        "read",
        "amount",
    );

    const fields = [
        ...baseFields,
        ...(canReadAmount ? ["amount", "adjusted_amount", "currency.name"] : []),
    ];

    const columns = [
        {
            id: "name",
            header: "Billable",
            accessorKey: "name",
            cell: ExtendedNameCell,
        },
        {
            id: "job.project.name",
            header: "Project",
            accessorKey: "job.project.name",
            cell: ProjectCell,
        },
        {
            id: "job",
            header: "Job",
            accessorKey: "job.name",
            cell: JobCell,
        },
        {
            id: "amount",
            header: "Amount",
            accessorKey: "amount",
            cell: AmountCell,
            field: "amount",
        },
        {
            id: "status",
            header: "Status",
            accessorKey: "status",
            cell: StatusCell,
        },
        {
            id: "type",
            header: "Type",
            accessorKey: "type",
            cell: TypeCell,
        },
        {
            id: "billing_date",
            header: "Billing Date / Extended Date",
            accessorKey: "billing_date",
            sortable: true,
            cell: ({ row, getValue }) => (
                <div className="flex flex-col">
                    {DateCell({ getValue })}
                    {row.original.extensions?.length > 0 && row.original.extensions?.[0]?.start_date && (
                        <span className="text-red-500 text-xs">
                            {DateCell({ getValue: () => row.original.extensions?.[0]?.start_date })}
                        </span>
                    )}
                </div>
            ),
        },
        {
            id: "due_date",
            header: "Due Date",
            accessorKey: "due_date",
            sortable: true,
            cell: DateCell,
        },
        {
            id: "extensions_count",
            accessorKey: "extensions",
            header: "Extensions Count",
            cell: ({ getValue }) => getValue()?.length,
        },
        {
            id: "extensions",
            header: "Extension justification",
            accessorKey: "extensions",
            cell: ({ row }) => {
                const extensions = row.original.extensions || [];
                const lastExtension = extensions.length > 0 ? extensions[extensions.length - 1] : null;
                return lastExtension?.justification || '';
            },
        },
        {
            id: "job.coordinator",
            header: "JC",
            accessorKey: "job.coordinator",
            cell: EmployeeCell,
        },
        {
            id: "job.project.coordinator",
            header: "PC",
            accessorKey: "job.project.coordinator",
            cell: EmployeeCell,
        },
        {
            id: "job.project.account_manager",
            header: "AM",
            accessorKey: "job.project.account_manager",
            cell: EmployeeCell,
        },
        {
            id: "job.project.sales_coordinator",
            header: "SC",
            accessorKey: "job.project.sales_coordinator",
            cell: EmployeeCell,
        },
    ];

    const currentDate = new Date().toISOString().split('T')[0]; // Get current date in YYYY-MM-DD format

    const defaultFilters = {
        job: {
            access: {
                employee: {
                    _eq: coordinatorId,
                },
                role: {
                    _in: ["account_manager", "sales_coordinator", "project_coordinator", "job_coordinator"]
                },
            },
        },
        due_date: {
            _lte: currentDate, // Filter for due dates that are less than or equal to today
        },
        status: { _nin: ["archived", "cancelled", "collected", "billed"] },

    };

    const deep = {
        extensions: {
            _sort: ["date_created"],
        },
    };

    return (
        <div key={coordinatorId} >
            <div className="flex-1 overflow-auto">
                <List
                    title="Pending Billables"
                    storageKey={`overview-pending-billables-${coordinatorId}`}
                    endPoint="billables"
                    filters={defaultFilters}
                    columns={columns}
                    meta={{ fields, deep }}
                    perPage={25}
                    href={(item) => item.extensions?.length > 0 ? ROUTE.BILLABLE_EXTENSIONS(item.id) : ROUTE.BILLABLE(item.id)}
                    hidePagination={true}
                    showSearch={false}
                />
            </div>
        </div>
    );
};

export default withPermissions(PendingBillablesList, {
    collection: "billables",
    action: "read",
});
